import {CREATE_WATCHLIST_FROM_SEARCH_PARAMS, GET_FILTER_DATA, SEARCH_LOGS} from "@/store/actions.type";
import {
	ADD_WATCHLIST,
	RESET_SEARCH_PARAMS,
	RESET_SEARCH_RESULTS,
	SET_LOADING,
	SET_SEARCH_FILTER_DATA,
	SET_SEARCH_PARAMS,
	SET_SEARCH_RESULTS,
	SET_SEARCH_RESULTS_EXHAUSTED,
	MUTATE_SNACK,
	SET_WATCHLIST,
	SET_SEARCH_TYPE,
	SET_SEARCH_REQUEST,
	SET_LOADING_LOGS,
	SET_SEARCH_DISPLAY_TYPE,
	SET_SEARCH_DISPLAY_TYPE_PALLET,
	SET_PALLET_LOGS_SEARCH_DISPLAY_TYPE
} from "@/store/mutations.type";
import {SearchService, WatchlistService} from "@/common/api";
import {INITIAL_LOG_FILTER_DATA, INITIAL_LOG_SEARCH_PARAMS, SEARCH_DISPLAY_TYPE, SEARCH_TYPE} from "@/common/constants";
import {logSearchPageSize} from "@/common/config";
import {REQUEST_ERROR} from "@/common/errorcodes";
import _isObject from "lodash/isObject";
import _map from "lodash/map";
import axios from 'axios';

const state = {
	searchType: SEARCH_TYPE.BLOCK,
	searchDisplayType: SEARCH_DISPLAY_TYPE.TEASER,
	searchDisplayTypePallet: SEARCH_DISPLAY_TYPE.TEASER,
	palletLogSearchDisplayType: SEARCH_DISPLAY_TYPE.TEASER,
	searchParams: {...INITIAL_LOG_SEARCH_PARAMS},
	searchResults: [],
	searchResultsExhaused: false,
	displayIncrement: logSearchPageSize,
	filterData: INITIAL_LOG_FILTER_DATA,
	loadingFilters: false,
	logSearchRequest: null
};

const getters = {
	searchType: state => state.searchType,
	searchDisplayType: state => state.searchDisplayType,
	searchDisplayTypePallet: state => state.searchDisplayTypePallet,
	palletLogSearchDisplayType: state => state.palletLogSearchDisplayType,
	logSearchParams: state => state.searchParams,
	logSearchResults: state => state.searchResults,
	logSearchResultsExhausted: state => state.searchResultsExhaused,
	filterData: state => state.filterData,
	loadingLogFilters: state => state.loadingFilters,
	logSearchRequest: state => state.logSearchRequest
};

const actions = {
	async [SEARCH_LOGS]({commit}, offset = 1) {
		if(state.logSearchRequest) {
			// cancel currently running requests
			state.logSearchRequest.cancel();
			commit(SET_SEARCH_REQUEST, null);
		}

		if (offset === 1) {
			// reset values for new searches
			commit(RESET_SEARCH_RESULTS);
			commit(SET_SEARCH_RESULTS_EXHAUSTED, false);
		}

		commit(SET_LOADING_LOGS, true);
		commit(SET_LOADING, true);
		commit(SET_SEARCH_PARAMS, {offset});

		commit(SET_SEARCH_REQUEST, axios.CancelToken.source());
		return SearchService.get(state.searchParams, {cancelToken: state.logSearchRequest.token})
			.then(({data}) => {
				if (data.length < logSearchPageSize) {
					// query delivered less data than expected, meaning the end of the current data set was reached
					commit(SET_SEARCH_RESULTS_EXHAUSTED, true);
				}

				if (_isObject(data)) {
					commit(SET_SEARCH_RESULTS, state.searchResults.concat(data));
				} else {
					commit(SET_SEARCH_RESULTS_EXHAUSTED, true);
					commit(MUTATE_SNACK, REQUEST_ERROR);
				}

				commit(SET_LOADING, false);
				commit(SET_LOADING_LOGS, false);
				commit(SET_SEARCH_REQUEST, null);
			})
			.catch(error => {
				if(!axios.isCancel(error)) {
					// prevent further searches after error triggered
					commit(SET_SEARCH_RESULTS_EXHAUSTED, true);
					commit(MUTATE_SNACK, REQUEST_ERROR);
				}
				commit(SET_SEARCH_REQUEST, null);
			})
	},
	async [GET_FILTER_DATA]({commit}) {
		const speciesUid = (_isObject(state.searchParams.species) ? state.searchParams.species.uid : parseInt(state.searchParams.species));

		if (speciesUid) {
			commit(SET_SEARCH_FILTER_DATA, INITIAL_LOG_FILTER_DATA);
			this.loadingFilters = true;
			return SearchService.getFilterData(speciesUid)
				.then(({data}) => {
					commit(SET_SEARCH_FILTER_DATA, data);
					this.loadingFilters = false;
				})
				.catch(error => {
					this.loadingFilters = false;
					commit(MUTATE_SNACK, REQUEST_ERROR);
				})
		}
	},
	async [CREATE_WATCHLIST_FROM_SEARCH_PARAMS]({commit}) {
		return WatchlistService.createWatchlistFromSearchParams(state.searchParams)
			.then(({data}) => {
				commit(MUTATE_SNACK, data.status);

				if (data.status && data.object) {
					commit(ADD_WATCHLIST, data.object);
					commit(SET_WATCHLIST, data.object);
				}
			})
			.catch(error => {
				commit(MUTATE_SNACK, REQUEST_ERROR);
			})
	}
};

const mutations = {
	[SET_SEARCH_TYPE](state, searchType) {
		state.searchType = searchType;
		localStorage.setItem('searchType', searchType);
	},
	[SET_SEARCH_DISPLAY_TYPE](state, searchDisplayType) {
		state.searchDisplayType = searchDisplayType;
		localStorage.setItem('searchDisplayType', searchDisplayType);
	},
	[SET_SEARCH_DISPLAY_TYPE_PALLET](state, searchDisplayType) {
		state.searchDisplayTypePallet = searchDisplayType;
		localStorage.setItem('searchDisplayTypePallet', searchDisplayType);
	},
	[SET_PALLET_LOGS_SEARCH_DISPLAY_TYPE](state, searchDisplayType) {
		state.palletLogSearchDisplayType = searchDisplayType;
		localStorage.setItem('palletLogSearchDisplayType', searchDisplayType);
	},
	[SET_SEARCH_PARAMS](state, searchParams) {
		// filter "null" string values
		for (let key in searchParams) {
			if (searchParams.hasOwnProperty(key)) {
				if (searchParams[key] === "null") {
					searchParams[key] = "";
				}

				if (['woodtype', 'characteristic', 'structure', 'searchType'].indexOf(key) >= 0) {
					searchParams[key] = parseInt(searchParams[key]);
				}

				if (key === 'qualities') {
					searchParams[key] = _map(searchParams[key], value => {
						return parseInt(value);
					});
				}
			}
		}

		state.searchParams = {...state.searchParams, ...searchParams};

		if (searchParams.offset === 1) {
			state.searchResults = [];
		}
	},

	[RESET_SEARCH_PARAMS](state, fullReset = false) {
		if (fullReset) {
			state.searchParams = {...INITIAL_LOG_SEARCH_PARAMS};
		} else {
			state.searchParams = {
				...state.searchParams,
				logNo: null,
				palletNo: null,
				thickness: null,
				characteristic: null,
				qualities: [],
				structure: null,
				limit: logSearchPageSize,
				offset: 1
			};
		}
	},

	[SET_SEARCH_RESULTS](state, searchResults) {
		state.searchResults = searchResults;
	},

	[RESET_SEARCH_RESULTS](state) {
		state.searchResults = [];
	},

	[SET_SEARCH_FILTER_DATA](state, filterData) {
		state.filterData = filterData;
	},

	[SET_SEARCH_RESULTS_EXHAUSTED](state, exhausted) {
		state.searchResultsExhaused = exhausted;
	},

	[SET_SEARCH_REQUEST](state, request) {
		state.logSearchRequest = request;
	}
};

export default {
	state,
	getters,
	actions,
	mutations
};